import { Loader2, LogIn, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '../hooks/useAuth';

function getInitials(name?: string, email?: string): string {
    if (name) {
        return name
            .split(' ')
            .map((part) => part[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
    }
    if (email) {
        return email[0].toUpperCase();
    }
    return '?';
}

export function AuthButton() {
    const { user, loading, signInWithGoogle, signOut } = useAuth();

    if (loading) {
        return (
            <div
                className="w-10 h-10 rounded-full bg-black/70 backdrop-blur-sm border border-white/20 flex items-center justify-center"
                data-ph-capture-attribute-auth-state="loading"
            >
                <Loader2 className="h-4 w-4 text-white/70 animate-spin" />
            </div>
        );
    }

    if (user) {
        const avatarUrl = user.user_metadata?.avatar_url;
        const fullName = user.user_metadata?.full_name;
        const initials = getInitials(fullName, user.email);

        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className="w-10 h-10 rounded-full border-2 border-white/80 shadow-lg hover:scale-110 transition-all duration-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-white/50"
                        title={fullName || user.email || 'Account'}
                        data-ph-capture-attribute-auth-state="authenticated"
                    >
                        {avatarUrl ? (
                            <img
                                src={avatarUrl}
                                alt={fullName || 'User avatar'}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <div className="w-full h-full bg-indigo-600 flex items-center justify-center text-white text-sm font-semibold">
                                {initials}
                            </div>
                        )}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    className="w-56 bg-white border border-gray-200 shadow-lg z-[9999]"
                >
                    <div className="px-3 py-2">
                        {fullName && (
                            <p className="text-sm font-medium text-gray-900 truncate">{fullName}</p>
                        )}
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={signOut}
                        className="cursor-pointer text-red-600 hover:bg-red-50 focus:bg-red-50 focus:text-red-600"
                        data-ph-capture-attribute-button-type="signout"
                    >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign out
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={signInWithGoogle}
            className="auth-signin-button flex items-center gap-2 bg-black/70 backdrop-blur-sm text-white border-white/20 hover:bg-black/80 hover:text-white"
            data-ph-capture-attribute-button-type="signin"
            data-ph-capture-attribute-auth-provider="google"
        >
            <LogIn className="h-4 w-4" />
            Sign in
        </Button>
    );
}
